/**
 * Check if a number is a power of 2
 */
export const isPowerOfTwo = (n: number): boolean => {
  return n > 0 && (n & (n - 1)) === 0;
};

/**
 * Get the next power of 2 greater than or equal to n
 */
export const nextPowerOfTwo = (n: number): number => {
  if (n <= 0) return 1;
  if (isPowerOfTwo(n)) return n;
  return Math.pow(2, Math.ceil(Math.log2(n)));
};

/**
 * Validate if the number of participants is valid for a double elimination tournament
 * Must be a power of 2 and at least 2 participants
 */
export const isValidParticipantCount = (count: number): boolean => {
  return count >= 2 && isPowerOfTwo(count);
};

/**
 * Get the number of rounds needed for winners bracket
 */
export const getWinnersBracketRounds = (participantCount: number): number => {
  if (!isPowerOfTwo(participantCount) || participantCount < 2) {
    throw new Error('Participant count must be a power of 2 and at least 2');
  }
  return Math.log2(participantCount);
};

/**
 * Get the number of rounds needed for losers bracket
 * For double elimination:
 * - 2 participants: 1 round (no losers bracket needed, just grand final)
 * - 4 participants: 2 rounds
 * - 8 participants: 5 rounds
 * - 16 participants: 11 rounds
 * Formula: (2 * winnersRounds) - 2 for 4+, but minimum 1
 */
export const getLosersBracketRounds = (participantCount: number): number => {
  if (participantCount === 2) {
    return 1; // Just grand final with bracket reset
  }
  const winnersRounds = getWinnersBracketRounds(participantCount);
  return (2 * winnersRounds) - 2;
};

/**
 * Get total number of matches in a double elimination tournament
 */
export const getTotalMatches = (participantCount: number): number => {
  if (!isPowerOfTwo(participantCount) || participantCount < 2) {
    throw new Error('Participant count must be a power of 2 and at least 2');
  }
  // Winners bracket: n - 1 matches
  // Losers bracket: n - 2 matches
  // Grand final: 1 match (potentially 2 if bracket reset needed)
  return (participantCount - 1) + (participantCount - 2) + 1;
};

/**
 * Validate if the number of participants is valid for a Swiss tournament
 * Swiss requires at least 4 participants (recommended minimum)
 */
export const isValidSwissParticipantCount = (count: number): boolean => {
  return count >= 4;
};

/**
 * Get the number of rounds for a Swiss tournament
 * Formula: ceil(log2(participants))
 */
export const getSwissRounds = (participantCount: number): number => {
  if (participantCount < 4) {
    throw new Error('Swiss system requires at least 4 participants');
  }
  return Math.ceil(Math.log2(participantCount));
};

/**
 * Get the total number of matches per round in Swiss
 * Formula: floor(participants / 2) - one player might get a bye if odd number
 */
export const getSwissMatchesPerRound = (participantCount: number): number => {
  return Math.floor(participantCount / 2);
};
